import { styled, alpha } from '@mui/material/styles';
import { Box, Card, Button } from '@mui/material';
import {
  getTableRowHoverOverlay,
  getTableRowSelectedHoverOverlay,
  getTableRowSelectedOverlay,
} from '../../../styles/theme/tableRowOverlays';

export const MainContainer = styled(Box)({
  display: 'flex',
  // flex:1 + minHeight:0 makes this fill only the space left by the header/progress
  // bar; height:100% here made it grow to content size, pushing the bottom of the
  // scrollable left column (and the branch-origin toggle) off-screen at 100% zoom.
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

export const LeftColumn = styled(Box)({
  flex: 1,
  minHeight: 0,
  padding: '24px',
  // Must be a plain block scroll region, NOT a flex column: MUI Cards default to
  // overflow:hidden, and a flex item with overflow!=visible gets an automatic
  // min-height of 0, so as a flex-column child the cards would shrink to fit and
  // clip their own content (e.g. the branch-origin toggle) instead of this column
  // scrolling. Keeping it a block lets the cards keep their natural height and
  // this column scroll, so nothing is ever cut off regardless of zoom.
  overflow: 'auto',
});

export const RightColumn = styled(Box)(({ theme }) => ({
  width: 400,
  padding: '24px',
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  borderLeft: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
  // auto (not hidden) so the summary/total never gets clipped when the fixed
  // header + summary exceed the available height at high zoom; the cart list
  // itself keeps its own inner scroll.
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
}));

export const LocationCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const ProductSearchCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
}));

export const SearchContainer = styled(Box)({
  position: 'relative',
  minHeight: 250,
});

export const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
}));

export const DropdownCard = styled(Card)(({ theme }) => ({
  maxHeight: 300,
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 4 : 2],
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

export const ProductItem = styled(Box)<{ isInCart: boolean }>(({ theme, isInCart }) => ({
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: isInCart ? getTableRowSelectedOverlay(theme) : 'transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: isInCart
      ? getTableRowSelectedHoverOverlay(theme)
      : getTableRowHoverOverlay(theme),
    boxShadow:
      theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
    borderColor: theme.palette.divider,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const CartHeaderBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const CartListBox = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

export const CartEmptyBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
}));

export const CartItemCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[1],
}));

export const SummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const ContinueButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.spacing(1.25),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9375rem',
  background: 'linear-gradient(to right, rgb(6, 182, 212), rgb(16, 185, 129))',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(to right, rgb(8, 145, 178), rgb(14, 159, 104))',
  },
  transition: 'all 0.2s',
  '&:active': {
    transform: 'scale(0.98)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:active': { transform: 'none' },
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
    transform: 'none',
  },
}));

export const WarningBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

export const WarningContent = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
  border: `1px solid ${alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.55 : 0.45)}`,
  borderRadius: '8px',
  padding: '8px 16px',
  width: '100%',
  color: theme.palette.text.primary,
}));

export const InfoBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

export const InfoContent = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
  border: `1px solid ${theme.palette.success.main}`,
  borderRadius: '8px',
  padding: '8px 16px',
  width: '100%',
  color: theme.palette.text.primary,
}));

export const LocationRadioLabel = styled(Box)<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  width: '100%',
  margin: 0,
  padding: theme.spacing(2),
  border: '1px solid',
  borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: theme.spacing(1),
  backgroundColor: isSelected
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.18)
      : alpha(theme.palette.primary.main, 0.08)
    : 'transparent',
}));

export const CustomerDisplayBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.14)
      : alpha(theme.palette.primary.main, 0.08),
  borderRadius: theme.spacing(1),
  border:
    theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.primary.main, 0.35)}` : 'none',
}));
